import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShoppingCart,
  Package,
  Star,
  ChevronRight,
  Phone,
  Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ORDER = [
  "All",
  "Infectious Disease",
  "Pregnancy",
  "Women's Health",
  "Urinalysis",
  "Urinalysis UTI",
  "GI / Fecal",
];

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "All");
  const featuredRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    const savedCart = localStorage.getItem("clearpath_cart");
    if (savedCart) setCart(JSON.parse(savedCart));
  }, []);

  // Scroll to featured product if linked from promo
  useEffect(() => {
    if (searchParams.get("featured") && featuredRef.current) {
      setTimeout(() => {
        featuredRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams, products]);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/shop/products`);
      const data = await res.json();
      setProducts(data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (productId) => {
    const updated = { ...cart, [productId]: (cart[productId] || 0) + 1 };
    setCart(updated);
    localStorage.setItem("clearpath_cart", JSON.stringify(updated));
    toast.success("Added to cart");
  };

  const removeFromCart = (productId) => {
    const updated = { ...cart };
    if (updated[productId] > 1) {
      updated[productId]--;
    } else {
      delete updated[productId];
    }
    setCart(updated);
    localStorage.setItem("clearpath_cart", JSON.stringify(updated));
  };

  const cartTotal = Object.entries(cart).reduce((sum, [id, qty]) => {
    const product = products.find((p) => p.id === id);
    return sum + (product ? product.price * qty : 0);
  }, 0);

  const cartCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);

  // Filter then pin featured products first
  const baseFiltered = activeCategory === "All"
    ? products
    : products.filter((p) => p.category === activeCategory);

  const filtered = [...baseFiltered].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    if (cat === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Clean Header */}
      <section className="bg-white border-b border-slate-200 pt-8 pb-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="shop-headline"
          >
            Product Catalog
          </h1>
          <p className="text-slate-500 text-sm">
            FDA-cleared rapid diagnostic test kits for professional and OTC use.
          </p>
        </div>
      </section>

      {/* Bulk / Custom Pricing Callout — right below header */}
      <section className="bg-sky-50 border-b border-sky-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800" data-testid="bulk-pricing-headline">
                Need larger quantities or custom pricing?
              </p>
              <p className="text-xs text-slate-600">
                Contact our team for volume pricing and product support.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href="tel:817-508-6232"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-800"
                data-testid="bulk-phone-cta"
              >
                <Phone className="w-3.5 h-3.5" />
                (817) 508-6232
              </a>
              <a
                href="mailto:sales@clearpathrapid.com"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-800"
                data-testid="bulk-email-cta"
              >
                <Mail className="w-3.5 h-3.5" />
                sales@clearpathrapid.com
              </a>
              <a
                href="mailto:sales@clearpathrapid.com?subject=Bulk%20Pricing%20Inquiry"
                className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                data-testid="bulk-contact-sales-btn"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {CATEGORY_ORDER.map((cat) => {
              const count = cat === "All" ? products.length : products.filter((p) => p.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? "bg-sky-600 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid={`category-filter-${cat.toLowerCase().replace(/[\s\/]/g, "-")}`}
                >
                  {cat} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product) => (
              <div
                key={product.id}
                ref={product.featured ? featuredRef : undefined}
              >
                <ProductCard
                  product={product}
                  cartQty={cart[product.id] || 0}
                  onAdd={() => addToCart(product.id)}
                  onRemove={() => removeFromCart(product.id)}
                />
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-16">No products in this category.</p>
          )}
        </div>
      </section>

      {/* Professional Use Notice */}
      <section className="py-6 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-xs text-slate-500">
            Products marked RX are for professional/clinical use only. OTC products are available for consumer purchase. All products are subject to availability. Contact ClearPath Rapid Diagnostics for ordering details.
          </p>
        </div>
      </section>

      {/* Sticky Cart Footer */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white py-4 px-6 z-40 shadow-2xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-medium" data-testid="cart-summary">
                {cartCount} item{cartCount !== 1 ? "s" : ""} &middot; ${cartTotal.toFixed(2)}
              </span>
            </div>
            <Button
              onClick={() => navigate("/checkout")}
              className="bg-sky-500 hover:bg-sky-600 text-white px-6"
              data-testid="footer-checkout-btn"
            >
              Proceed to Checkout
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, cartQty, onAdd, onRemove }) {
  return (
    <div
      className={`bg-white rounded-xl border ${
        product.featured ? "border-orange-300 ring-2 ring-orange-200" : "border-slate-200"
      } overflow-hidden flex flex-col transition-shadow hover:shadow-md`}
      data-testid={`shop-product-${product.id}`}
    >
      {/* Featured badge */}
      {product.featured && (
        <div className="bg-orange-500 text-white text-xs font-bold tracking-wider text-center py-1.5 px-3">
          <Star className="w-3 h-3 inline -mt-0.5 mr-1" />
          SPECIAL OFFER
        </div>
      )}

      {/* Image */}
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            data-testid={`shop-product-image-${product.id}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <Package className="w-12 h-12 text-slate-400" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Category + Specimen */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-semibold tracking-wider text-sky-600 uppercase">
            {product.category}
          </span>
          {product.specimen && (
            <span className="text-[10px] text-slate-400">&middot; {product.specimen}</span>
          )}
        </div>

        {/* Name */}
        <h3
          className="text-sm font-bold text-slate-900 mb-1 leading-tight"
          style={{ fontFamily: "Manrope, sans-serif" }}
          data-testid={`product-name-${product.id}`}
        >
          {product.name}
        </h3>

        {/* Item code + Format */}
        <div className="text-xs text-slate-500 mb-3 space-y-0.5">
          <p data-testid={`product-sku-${product.id}`}>
            <span className="font-medium text-slate-600">Item:</span> {product.item_code}
          </p>
          <p>
            <span className="font-medium text-slate-600">Format:</span> {product.format}
          </p>
        </div>

        {/* Pricing block */}
        <div className="mt-auto">
          {/* Package price */}
          <div className="mb-1">
            <span
              className={`text-xl font-bold ${product.featured ? "text-orange-600" : "text-slate-900"}`}
              data-testid={`product-price-${product.id}`}
            >
              ${product.price.toFixed(2)}
            </span>
            <span className="text-sm text-slate-500 ml-1">
              per {product.packaging_unit}
            </span>
          </div>

          {/* Per-test price */}
          <p
            className={`text-sm font-semibold mb-1 ${product.featured ? "text-orange-500" : "text-sky-600"}`}
            data-testid={`product-per-test-${product.id}`}
          >
            ${product.price_per_test.toFixed(2)} per test
          </p>

          {/* Pack quantity */}
          <p className="text-xs text-slate-500 mb-3" data-testid={`product-pack-qty-${product.id}`}>
            {product.packaging_display}
          </p>

          {/* Cart controls */}
          {cartQty > 0 ? (
            <div className="flex items-center gap-2">
              <Button
                onClick={onRemove}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                data-testid={`remove-from-cart-${product.id}`}
              >
                -
              </Button>
              <span
                className="text-sm font-bold text-slate-900 w-6 text-center"
                data-testid={`cart-qty-${product.id}`}
              >
                {cartQty}
              </span>
              <Button
                onClick={onAdd}
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                data-testid={`add-more-${product.id}`}
              >
                +
              </Button>
              <span className="text-xs text-slate-500 ml-auto">
                ${(product.price * cartQty).toFixed(2)}
              </span>
            </div>
          ) : (
            <Button
              onClick={onAdd}
              className={`w-full text-sm ${
                product.featured
                  ? "bg-orange-500 hover:bg-orange-600 text-white"
                  : "bg-sky-600 hover:bg-sky-700 text-white"
              }`}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
